import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Button from 'app/ui/components/atoms/button';
import { ChevronLeft } from 'app/ui/components/atoms/icons/chevron-left';
import ChevronRight from 'app/ui/components/atoms/icons/chevron-right';
import { Close } from 'app/ui/components/atoms/icons/close';
import { ThumbsDown } from 'app/ui/components/atoms/icons/thumbs-down';
import { ThumbsUp } from 'app/ui/components/atoms/icons/thumbs-up';
import Tick from 'app/ui/components/atoms/icons/tick';
import Typography from 'app/ui/components/atoms/typography';
import {
  FEEDBACK_TAG_GROUPS,
  type FeedbackTag,
  FeedbackTagCategory,
} from 'app/ui/components/molecules/workout-feedback/feedback-tags';
import { Modal } from 'app/ui/components/molecules/workout-feedback/modal';
import { StarRating } from 'app/ui/components/molecules/workout-feedback/star-rating';
import { Textarea } from 'app/ui/components/molecules/workout-feedback/textarea';
import {
  type FeedbackStep,
  FREE_TEXT_MAX_LENGTH,
  getFeedbackStepTracker,
  hasFeedbackFriction,
  type RunAgainAnswer,
  SUCCESS_AUTO_DISMISS_MS,
  type WorkoutFeedbackLabels,
  type WorkoutFeedbackPayload,
} from 'app/ui/components/molecules/workout-feedback/types';

interface WorkoutFeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labels: WorkoutFeedbackLabels;
  onSubmit: (payload: WorkoutFeedbackPayload) => Promise<void>;
}

const TAG_STEP_CATEGORY: Record<'gym' | 'programming' | 'whiteboard', FeedbackTagCategory> = {
  gym: FeedbackTagCategory.GymEnvironment,
  programming: FeedbackTagCategory.Programming,
  whiteboard: FeedbackTagCategory.Whiteboard,
};

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: ${({ theme: { spacing } }) => `${spacing(6)} ${spacing(8)} ${spacing(3)}`};
`;

const CloseIconButton = styled.button`
  background: transparent;
  border: none;
  padding: ${({ theme: { spacing } }) => spacing(1)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme: { colors } }) => colors.knop};

  &:hover {
    color: ${({ theme: { colors } }) => colors.white};
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(5)};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme: { spacing } }) => `${spacing(4)} ${spacing(8)} ${spacing(6)}`};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  padding: ${({ theme: { spacing } }) => `${spacing(2)} 0`};
`;

const SectionLabel = styled.span`
  ${({ theme: { typography, colors } }) => `
    font-family: ${typography.poppins};
    font-size: ${typography.large};
    font-weight: ${typography.bold};
    color: ${colors.white};
    text-align: center;
  `}
`;

const SectionSubLabel = styled.span`
  ${({ theme: { typography, colors } }) => `
    font-family: ${typography.poppins};
    font-size: ${typography.small};
    font-weight: ${typography.light};
    color: ${colors.white};
  `}
`;

const RatingValue = styled.span`
  ${({ theme: { typography, colors } }) => `
    font-family: ${typography.poppins};
    font-size: ${typography.tiny};
    font-weight: ${typography.bold};
    color: ${colors.white};
  `}
`;

const RunAgainRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme: { spacing } }) => spacing(4)};
`;

interface ToggleButtonProps {
  $active: boolean;
  $variant: 'yes' | 'no';
}

const ToggleButton = styled.button<ToggleButtonProps>`
  ${({ theme: { colors, spacing, typography }, $active, $variant }) => {
    const activeBg = $variant === 'yes' ? colors.greenWithTransparency : colors.redWithTransparency;
    const activeBorder = $variant === 'yes' ? colors.green : colors.firebreathingRed;
    const activeText = $variant === 'yes' ? colors.green : colors.firebreathingRed;
    return `
      display: inline-flex;
      align-items: center;
      gap: ${spacing(2)};
      padding: ${spacing(2)} ${spacing(4)};
      border-radius: 9999px;
      background: ${$active ? activeBg : colors.blackSwan};
      border: 0.0625rem solid ${$active ? activeBorder : 'transparent'};
      color: ${$active ? activeText : colors.white};
      cursor: pointer;
      font-family: ${typography.poppins};
      font-size: ${typography.tiny};
      font-weight: ${typography.bold};
      transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

      svg {
        width: ${spacing(5)};
        height: ${spacing(5)};
      }

      &:hover {
        background: ${$active ? activeBg : colors.devilsGray};
      }
    `;
  }}
`;

const TagStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(4)};
`;

const TagQuestion = styled.span`
  ${({ theme: { typography, colors } }) => `
    font-family: ${typography.poppins};
    font-size: ${typography.small};
    font-weight: ${typography.light};
    color: ${colors.white};
  `}
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme: { spacing } }) => spacing(2.5)};
`;

const TagPill = styled.button<{ $active: boolean }>`
  ${({ theme: { colors, spacing, typography }, $active }) => `
    display: inline-flex;
    align-items: center;
    min-height: ${spacing(8)};
    padding: ${spacing(1.5)} ${spacing(3.5)};
    border-radius: 9999px;
    background: ${$active ? colors.focoltone : colors.blackSwan};
    color: ${$active ? colors.black : colors.white};
    border: 0.0625rem solid ${$active ? colors.focoltone : colors.blackSwan};
    cursor: pointer;
    font-family: ${typography.poppins};
    font-size: ${typography.tiny};
    font-weight: ${typography.bold};
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: ${$active ? colors.focoltone : colors.devilsGray};
    }
  `}
`;

const SelectAllCaption = styled.span`
  ${({ theme: { typography, colors } }) => `
    font-family: ${typography.poppins};
    font-size: ${typography.tiny};
    font-weight: ${typography.light};
    color: ${colors.knop};
  `}
`;

const FreeTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(2)};
`;

const FreeTextLabel = styled.span`
  ${({ theme: { typography, colors } }) => `
    font-family: ${typography.poppins};
    font-size: ${typography.xsmall};
    font-weight: ${typography.bold};
    color: ${colors.white};
  `}
`;

const TextareaWrapper = styled.div`
  position: relative;
`;

const CharCount = styled.span`
  ${({ theme: { typography, colors, spacing } }) => `
    position: absolute;
    right: ${spacing(4)};
    bottom: ${spacing(3)};
    pointer-events: none;
    font-family: ${typography.poppins};
    font-size: ${typography.tiny};
    color: ${colors.knop};
  `}
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  flex-shrink: 0;
  padding: ${({ theme: { spacing } }) => `${spacing(4)} ${spacing(8)} ${spacing(6)}`};
`;

const TrackerWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(1.5)};
`;

const TrackerDot = styled.span<{ $active: boolean }>`
  ${({ theme: { colors, spacing }, $active }) => `
    height: ${spacing(2.5)};
    width: ${$active ? spacing(5) : spacing(2.5)};
    border-radius: 9999px;
    background: ${$active ? colors.focoltone : colors.blackSwan};
    transition: width 0.2s ease, background 0.2s ease;
  `}
`;

const TrackerLabel = styled.span`
  ${({ theme: { typography, colors, spacing } }) => `
    margin-left: ${spacing(2)};
    font-family: ${typography.poppins};
    font-size: ${typography.tiny};
    color: ${colors.knop};
  `}
`;

const FooterSpacer = styled.span`
  flex: 1;
`;

const FooterButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(3)};
`;

const SuccessWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  padding: ${({ theme: { spacing } }) => spacing(8)};
  min-height: 15rem;
  text-align: center;
`;

const SuccessIconCircle = styled.div`
  ${({ theme: { colors, spacing } }) => `
    width: ${spacing(12)};
    height: ${spacing(12)};
    border-radius: 9999px;
    background: ${colors.focoltone};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: ${spacing(7)};
      height: ${spacing(7)};
    }
  `}
`;

const SuccessMessage = styled.p`
  ${({ theme: { typography, colors } }) => `
    margin: 0;
    font-family: ${typography.poppins};
    font-size: ${typography.small};
    font-weight: ${typography.bold};
    color: ${colors.white};
  `}
`;

interface StepTrackerProps {
  current: number;
  total: number;
  label: string;
}

const StepTracker = ({ current, total, label }: StepTrackerProps) => (
  <TrackerWrapper>
    {Array.from({ length: total }, (_, index) => index + 1).map((index) => (
      <TrackerDot key={index} $active={index === current} />
    ))}
    <TrackerLabel>{label}</TrackerLabel>
  </TrackerWrapper>
);

export const WorkoutFeedbackModal = ({ open, onOpenChange, labels, onSubmit }: WorkoutFeedbackModalProps) => {
  const { colors } = useTheme();
  const [step, setStep] = useState<FeedbackStep>('ratings');
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const [easeOfFollowing, setEaseOfFollowing] = useState<number | null>(null);
  const [runAgain, setRunAgain] = useState<RunAgainAnswer | null>(null);
  const [selectedTags, setSelectedTags] = useState<FeedbackTag[]>([]);
  const [freeText, setFreeText] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('ratings');
      setOverallRating(null);
      setEaseOfFollowing(null);
      setRunAgain(null);
      setSelectedTags([]);
      setFreeText('');
      setShowSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!showSuccess) {
      return;
    }
    const timer = setTimeout(() => onOpenChange(false), SUCCESS_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [showSuccess, onOpenChange]);

  const friction = hasFeedbackFriction({ overallRating, easeOfFollowing, runAgain });
  const hasAnyAnswer = overallRating !== null || easeOfFollowing !== null || runAgain !== null;
  const tracker = getFeedbackStepTracker(step);

  const buildPayload = (skippedByUser: boolean): WorkoutFeedbackPayload => ({
    overallRating,
    easeOfFollowing,
    runAgain,
    feedbackTags: skippedByUser ? [] : selectedTags,
    freeText: !skippedByUser && freeText.trim().length > 0 ? freeText.trim() : null,
    skipped: skippedByUser,
  });

  const handleDismiss = () => {
    onSubmit(buildPayload(true)).catch(() => undefined);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(buildPayload(false));
      setShowSuccess(true);
    } catch {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (step === 'ratings') {
      if (friction) {
        setStep('gym');
      } else {
        setSelectedTags([]);
        setStep('free-text');
      }
      return;
    }
    if (step === 'gym') {
      setStep('programming');
      return;
    }
    if (step === 'programming') {
      setStep('whiteboard');
    }
  };

  const handleBack = () => {
    if (step === 'gym') {
      setStep('ratings');
      return;
    }
    if (step === 'programming') {
      setStep('gym');
      return;
    }
    if (step === 'whiteboard') {
      setStep('programming');
    }
  };

  const toggleTag = (tag: FeedbackTag) =>
    setSelectedTags((current) =>
      current.includes(tag) ? current.filter((value) => value !== tag) : [...current, tag],
    );

  const renderTagStep = (stepKey: 'gym' | 'programming' | 'whiteboard') => {
    const category = TAG_STEP_CATEGORY[stepKey];
    const group = FEEDBACK_TAG_GROUPS.find((entry) => entry.category === category);
    if (!group) {
      return null;
    }
    return (
      <TagStep>
        <TagQuestion>{labels.categoryQuestionLabel(category)}</TagQuestion>
        <TagRow>
          {group.tags.map((tag) => {
            const active = selectedTags.includes(tag);
            return (
              <TagPill key={tag} type="button" $active={active} aria-pressed={active} onClick={() => toggleTag(tag)}>
                {labels.tagLabel(tag)}
              </TagPill>
            );
          })}
        </TagRow>
        <SelectAllCaption>{labels.selectAllThatApply}</SelectAllCaption>
      </TagStep>
    );
  };

  const renderFreeTextBlock = () => (
    <FreeTextBlock>
      <FreeTextLabel>{labels.shareFeedbackLabel}</FreeTextLabel>
      <TextareaWrapper>
        <Textarea
          rows={3}
          maxLength={FREE_TEXT_MAX_LENGTH}
          placeholder={labels.freeTextPlaceholder}
          value={freeText}
          onChange={(event) => setFreeText(event.target.value)}
        />
        <CharCount>{labels.charCountLabel(freeText.length, FREE_TEXT_MAX_LENGTH)}</CharCount>
      </TextareaWrapper>
    </FreeTextBlock>
  );

  const isFinalStep = step === 'whiteboard' || step === 'free-text';
  const showBack = step !== 'ratings' && step !== 'free-text';
  const isNextDisabled = step === 'ratings' && !hasAnyAnswer;

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {showSuccess ? (
        <SuccessWrapper>
          <SuccessIconCircle>
            <Tick color={colors.black} />
          </SuccessIconCircle>
          <SuccessMessage>{labels.successMessage}</SuccessMessage>
          <Button type="secondary-white" size="sm" label={labels.closeLabel} onClick={() => onOpenChange(false)} />
        </SuccessWrapper>
      ) : (
        <>
          <Header>
            <Typography variant="h4">{labels.title}</Typography>
            <CloseIconButton type="button" onClick={handleDismiss} aria-label={labels.closeLabel}>
              <Close color={colors.knop} />
            </CloseIconButton>
          </Header>

          <Body>
            {step === 'ratings' ? (
              <>
                <Section>
                  <SectionLabel>{labels.overallRatingLabel}</SectionLabel>
                  <StarRating value={overallRating} onChange={setOverallRating} ariaLabel={labels.overallRatingLabel} />
                  {typeof overallRating === 'number' ? (
                    <RatingValue>{labels.ratingValueLabel(overallRating)}</RatingValue>
                  ) : null}
                </Section>

                <Section>
                  <SectionSubLabel>{labels.easeOfFollowingLabel}</SectionSubLabel>
                  <StarRating
                    value={easeOfFollowing}
                    onChange={setEaseOfFollowing}
                    ariaLabel={labels.easeOfFollowingLabel}
                  />
                  {typeof easeOfFollowing === 'number' ? (
                    <RatingValue>{labels.easeRatingValueLabel(easeOfFollowing)}</RatingValue>
                  ) : null}
                </Section>

                <Section>
                  <SectionSubLabel>{labels.runAgainLabel}</SectionSubLabel>
                  <RunAgainRow>
                    <ToggleButton
                      type="button"
                      $active={runAgain === 'YES'}
                      $variant="yes"
                      aria-pressed={runAgain === 'YES'}
                      onClick={() => setRunAgain('YES')}
                    >
                      <ThumbsUp color={runAgain === 'YES' ? colors.green : colors.white} />
                      <span>{labels.yes}</span>
                    </ToggleButton>
                    <ToggleButton
                      type="button"
                      $active={runAgain === 'NO'}
                      $variant="no"
                      aria-pressed={runAgain === 'NO'}
                      onClick={() => setRunAgain('NO')}
                    >
                      <ThumbsDown color={runAgain === 'NO' ? colors.firebreathingRed : colors.white} />
                      <span>{labels.no}</span>
                    </ToggleButton>
                  </RunAgainRow>
                </Section>
              </>
            ) : null}

            {step === 'gym' ? renderTagStep('gym') : null}
            {step === 'programming' ? renderTagStep('programming') : null}
            {step === 'whiteboard' ? (
              <>
                {renderTagStep('whiteboard')}
                {renderFreeTextBlock()}
              </>
            ) : null}
            {step === 'free-text' ? renderFreeTextBlock() : null}
          </Body>

          <Footer>
            {tracker ? (
              <StepTracker
                current={tracker.current}
                total={tracker.total}
                label={labels.stepIndicatorLabel(tracker.current, tracker.total)}
              />
            ) : (
              <FooterSpacer />
            )}
            <FooterButtons>
              {showBack ? (
                <Button
                  type="secondary-white"
                  size="sm"
                  label={labels.back}
                  iconPosition="left"
                  icon={<ChevronLeft color={colors.white} />}
                  onClick={handleBack}
                />
              ) : null}
              {isFinalStep ? (
                <Button
                  type="primary"
                  size="sm"
                  label={labels.submit}
                  state={submitting ? 'disabled' : 'default'}
                  onClick={handleSubmit}
                />
              ) : (
                <Button
                  type="primary"
                  size="sm"
                  label={labels.next}
                  iconPosition="right"
                  icon={<ChevronRight color={colors.black} />}
                  state={isNextDisabled ? 'disabled' : 'default'}
                  onClick={handleNext}
                />
              )}
            </FooterButtons>
          </Footer>
        </>
      )}
    </Modal>
  );
};
